import { LoadingButton } from '@mui/lab';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../../api/login';
import { getCart, getShoppingLists, setCustomer } from '../../api/user';
import {
  setCart,
  setCartId,
  setCartObject,
  setCartSubtotal,
  setCartTaxes,
  setCartTotal,
} from '../../state/cartSlice';
import { setShoppingLists } from '../../state/shoppingListsSlice';
import {
  setAddresses,
  setCompanyInfo,
  setPersonalInfo,
  setRole,
  setSelectedClient,
  setSellerInfo,
  setUser,
  setUserId,
} from '../../state/userSlice';
import PopupNotification from '../PopupNotification/PopupNotification';

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [notificationProps, setNotificationProps] = useState({
    isOpened: false,
  });
  const [userData, setUserData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [_cookies, setCookie, _removeCookie] = useCookies(['userToken']);

  function handleChange(e) {
    const { name, value } = e.target;

    setUserData((values) => ({
      ...values,
      [name]: value,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    setIsLoading(true);

    if (!userData.email || !userData.password) {
      setIsLoading(false);

      return;
    }

    login(userData)
      .then(({ token, user, expiresIn }) => {
        const expiresInSeconds = parseInt(expiresIn.charAt(0)) * 24 * 60 * 60;
        const userRole = user.user_role.toLowerCase();
        let sellerCustomers = null;

        //  set user token
        setCookie('userToken', token, {
          path: '/',
          maxAge: expiresInSeconds,
          secure: true,
          sameSite: true,
        });

        dispatch(setCompanyInfo(user.companyInfo));
        dispatch(setPersonalInfo(user.personalInfo));
        dispatch(setAddresses(user.addresses));
        dispatch(setRole(userRole));
        dispatch(setUserId(user.id));
        //  set customer discount
        dispatch(setUser({ customerDiscount: user?.customer_discount }));

        const sellerInfo = {
          name:
            userRole === 'customer' ? user.sellerName : user.personalInfo.name,
          email:
            userRole === 'customer'
              ? user.sellerEmail
              : user.personalInfo.email,
          phone:
            userRole === 'customer'
              ? user.sellerPhone
              : user.personalInfo.phone,
          clients: user.customers,
        };

        //  if it is a 'customer'
        if (userRole === 'customer') {
          sellerInfo.paymentTime = user.payment_deadlines;
        }

        dispatch(setSellerInfo(sellerInfo));

        //  if it is a 'seller'
        if (userRole === 'seller' && !!user?.customers.length) {
          const firstCustomer = user?.customers?.[0];
          dispatch(setSelectedClient(firstCustomer?.companyInfo?.name));

          //  set first customer as an active
          setCustomer({ customerId: firstCustomer?.id });

          if (user?.customers.length !== 0) {
            sellerCustomers = user?.customers;
          }
        }

        if (
          userRole === 'customer' ||
          (userRole === 'seller' && sellerCustomers !== null)
        ) {
          setIsLoading(true);

          //  get cart items
          getCart(
            userRole === 'customer'
              ? user?.cart_id
              : sellerCustomers?.[0]?.cart_id
          )
            .then(
              ({
                _id,
                delayedPayments,
                cart_items,
                taxes,
                subtotal,
                total,
              }) => {
                dispatch(setCart(cart_items));
                dispatch(setCartId(_id));
                dispatch(setCartTaxes(taxes));
                dispatch(setCartSubtotal(subtotal));
                dispatch(setCartTotal(total));
                dispatch(
                  setCartObject({
                    delayedPayments: delayedPayments,
                  })
                );

                return (
                  //  get shipping lists
                  getShoppingLists()
                    .then((lists) => {
                      dispatch(setShoppingLists(lists));

                      if (isLoading) setIsLoading(false);
                    })
                    .catch(({ response: { status } }) => {
                      if (status === 401) navigate('/login');
                    })
                );
              }
            )
            .catch(({ response: { status } }) => {
              if (status === 401) navigate('/login');
            });
        }

        return navigate('/', { replace: true });
      })
      .catch(({ response: { status, data } }) => {
        const { msg } = data;
        if (status >= 400) {
          setNotificationProps((prevState) => ({
            ...prevState,
            isOpened: true,
            type: 'error',
            errorMessage: msg,
          }));
        }
        setIsLoading(false);
      });
  }

  return (
    <>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TextField
          fullWidth
          label='Email / Número de cliente'
          type='text'
          name='email'
          required
          value={userData.email}
          onChange={handleChange}
        />

        <TextField
          fullWidth
          name='password'
          label='Palavra-passe'
          type={showPassword ? 'text' : 'password'}
          required
          value={userData.password}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          variant='contained'
          type='submit'
          title='Iniciar sessão'
          sx={{
            marginTop: 3,
          }}
          loading={isLoading}
        >
          Iniciar sessão
        </LoadingButton>
      </Box>

      <PopupNotification
        notificationProps={notificationProps}
        setNotificationProps={setNotificationProps}
      />
    </>
  );
};

export default LoginForm;
